'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, TextField, BlockStack, FormLayout, InlineStack, Banner } from '@shopify/polaris';
import * as MediaAssetAPI from '@/api/media-asset';
import {
  useCompletedMediaAsset,
  useMediaAssetPollingContext,
  MediaAssetPollingProvider,
} from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import { MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import DismissibleBanner from '@/components/DismissibleBanner';
import { BannerState, SessionBanner } from '@/types/banners';

interface TextToVideoProps {
  productId?: string;
  productTitle?: string;
  initialPrompt?: string;
  hideUseAction?: boolean;
}

export const TextToVideoComponent = ({
  productId,
  productTitle,
  initialPrompt = '',
  hideUseAction = false,
}: TextToVideoProps) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const { addMediaAssetToPollingQueue } = useMediaAssetPollingContext();
  const { appKey } = useCurrentApp();
  const generateMutation = useMutation({
    mutationFn: () =>
      MediaAssetAPI.generateTextToVideo({
        appKey,
        videoPrompt: prompt,
        productId,
        productTitle,
      }),
  });

  const handlePromptChange = (value: string) => {
    setPrompt(value);
  };

  const handleGenerate = () => {
    generateMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (!data) return;
        addMediaAssetToPollingQueue(data);
      },
    });
  };

  const { isLoading, mediaAsset: initialMediaAsset, hasError } = useCompletedMediaAsset(generateMutation.data);

  const mediaAsset = initialMediaAsset as StandaloneMediaAsset;

  const isGenerating = generateMutation.isPending || isLoading;
  const isError = !!generateMutation.error || hasError;

  const generatedMediaAsset = useMemo(() => {
    if (mediaAsset?.status === MediaAssetStatus.DONE) {
      return mediaAsset;
    }

    return generateMutation.data;
  }, [mediaAsset, generateMutation?.data]);

  return (
    <BlockStack gap="600">
      <FormLayout>
        <TextField
          label="Video Prompt"
          labelHidden
          value={prompt}
          onChange={handlePromptChange}
          autoComplete="off"
          placeholder="Enter a prompt..."
          disabled={isGenerating}
          connectedRight={
            <Button
              size="large"
              variant="primary"
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              loading={isGenerating}
            >
              Generate
            </Button>
          }
        />
      </FormLayout>

      <DismissibleBanner
        tone="critical"
        title="Generation Failed"
        sessionBanner={SessionBanner.TextToVideoGenerationError}
        showBanner={isError}
      >
        Error generating video. Please try again or contact support.
      </DismissibleBanner>

      <DismissibleBanner tone="info" showBanner={isGenerating} sessionBanner={SessionBanner.FinalVideoAvailable}>
        Final video will be also available in &quot;My creations&quot; tab
      </DismissibleBanner>
      {!isError && generatedMediaAsset && (
        <InlineStack align="center">
          <MediaAssetCard media={generatedMediaAsset} hideUseAction={hideUseAction} />
        </InlineStack>
      )}
    </BlockStack>
  );
};

export const TextToVideo = (props) => {
  const queryClient = useQueryClient();
  const initStateFromServerPage = useRef(false);

  if (!initStateFromServerPage.current) {
    initStateFromServerPage.current = true;
    if (props.isFinalVideoAvailableBannerHidden) {
      queryClient.setQueryData(['bannerState', SessionBanner.FinalVideoAvailable], BannerState.CLOSED);
    }
  }

  return (
    <MediaAssetPollingProvider>
      <TextToVideoComponent {...props} />
    </MediaAssetPollingProvider>
  );
};
